import { fetchWithAwsWaf } from 'lib/security/awsWaf';
import config from 'utils/config';
import { isBuildPhase } from 'utils/isBuildPhase';
import logProviderFactory from 'utils/logger/logProviderFactory';
import { parseRetryAfterMs } from 'utils/retryAfter';

// The shared demo backend occasionally responds slower than production's own API —
// give it more room before aborting instead of surfacing a spurious timeout locally.
const DEFAULT_TIMEOUT_MS = config.backendEnv === 'demo' ? 60_000 : 10_000;

// Escalating backoff for rate-limited prerenders, jittered below.
const BUILD_RETRY_DELAYS_MS = [500, 1500, 5000];

// Ceiling on a single wait, so honouring a large `Retry-After` can't stall a prerender past
// `staticPageGenerationTimeout` (180s in next.config.js). Three capped waits plus four
// DEFAULT_TIMEOUT_MS requests stays comfortably inside that budget.
const MAX_BUILD_RETRY_DELAY_MS = 15_000;

/**
 * Wait for one build-phase retry attempt.
 *
 * Jittered because the backoff exists to survive a *self-inflicted* burst: `next build` fans
 * out across `experimental.cpus` workers, so a rate limit tends to reject several concurrent
 * prerenders in the same instant. A fixed schedule then marches all of them back in lockstep
 * and they collide again. Spreading each wait over [base, 2×base) decorrelates them — a
 * retry never fires sooner than the base schedule, and worst-case added wait roughly doubles
 * to ~14s for a request that would otherwise ship a degraded page.
 *
 * `Retry-After`, when the upstream sends one, is a directive rather than a hint: retrying
 * before it elapses just burns an attempt. Treat it as a floor on the jittered delay.
 */
function buildRetryDelayMs(response: Response, attempt: number): number {
    const base = BUILD_RETRY_DELAYS_MS[attempt];
    const jittered = base + Math.random() * base;
    const retryAfter = parseRetryAfterMs(response) ?? 0;
    return Math.round(Math.min(Math.max(jittered, retryAfter), MAX_BUILD_RETRY_DELAY_MS));
}

// undici's default connect timeout (10s) can trip before DEFAULT_TIMEOUT_MS ever
// gets a chance to, since it bounds the TCP connect phase separately from the
// AbortSignal below. Raise it to match when talking to the shared demo backend.
// This file is also bundled into client components (e.g. PerpsListClient), so the
// `undici` import must stay dynamic and Node-only — a static import drags in
// `node:net`, which breaks the browser bundle.
//
// Kick the import off at module load, but expose it as a promise so `fetchServerSide`
// can await it before issuing the first request. A fire-and-forget `.then()` races the
// first server-side fetch, which could still connect under undici's default 10s timeout.
const dispatcherReady: Promise<void> | null =
    typeof window === 'undefined' && config.backendEnv === 'demo'
        ? import('undici')
              .then(({ Agent, setGlobalDispatcher }) => {
                  setGlobalDispatcher(new Agent({ connect: { timeout: DEFAULT_TIMEOUT_MS } }));
              })
              // A setup failure shouldn't block the fetch — fall back to undici's default
              // dispatcher rather than rejecting and failing every server-side request.
              .catch((e) => {
                  logProviderFactory.logError(`Failed to raise undici connect timeout for demo backend: ${e}`);
              })
        : null;

export const fetchServerSide = async (url: string, options?: RequestInit) => {
    try {
        if (dispatcherReady) {
            await dispatcherReady;
        }
        if (url.includes('.gz')) {
            options = {
                ...options,
                headers: {
                    ...options?.headers,
                    'Accept-Encoding': 'gzip',
                },
            };
        }
        const send = () =>
            fetchWithAwsWaf(url, {
                ...options,
                signal: options?.signal ?? AbortSignal.timeout(DEFAULT_TIMEOUT_MS),
            });

        if (!isBuildPhase) return send();

        // `next build` prerenders hundreds of pages in parallel workers, which bursts far
        // harder than production traffic and gets rate-limited. That used to be papered over
        // downstream — the cached-render guards swallowed the failure and shipped a
        // degraded page — but a prerender IS the first cached response, so the honest
        // options are "fail the deploy" or "don't get rate-limited". Retry with backoff so a
        // transient 429/5xx is neither baked in nor fatal. Build-phase only: runtime requests
        // are unaffected, since there a failure is one request rather than a stored artifact.
        let response = await send();
        for (let attempt = 0; attempt < BUILD_RETRY_DELAYS_MS.length; attempt++) {
            if (!response || (response.status !== 429 && response.status < 500)) break;
            const delay = buildRetryDelayMs(response, attempt);
            logProviderFactory.logError(
                `Prerender got ${response.status} from ${url} — retrying in ${delay}ms (attempt ${attempt + 1}/${BUILD_RETRY_DELAYS_MS.length})`,
            );
            await new Promise((resolve) => setTimeout(resolve, delay));
            response = await send();
        }
        return response;
    } catch (e) {
        const error = `Error fetching server-side data from ${url}: ${e}`;
        logProviderFactory.logError(error);
        return null;
    }
};
